/**
 * Auth Service
 *
 * Wraps Firebase Auth client SDK for Ice Master authentication.
 * Provides email/password, Google OAuth, and token management.
 */

import {
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signOut as firebaseSignOut,
  GoogleAuthProvider,
  type User,
  type Unsubscribe,
} from 'firebase/auth';
import { initializeApp, getApps } from 'firebase/app';

// =============================================================================
// Firebase Initialization (singleton)
// =============================================================================

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

const isConfigured = Boolean(
  firebaseConfig.apiKey &&
  firebaseConfig.authDomain &&
  firebaseConfig.projectId
);

const app = isConfigured
  ? getApps().length === 0
    ? initializeApp(firebaseConfig)
    : getApps()[0]
  : null;

const auth = app ? getAuth(app) : null;

// =============================================================================
// Service Methods
// =============================================================================

/**
 * Sign in with email and password.
 */
async function signInWithEmail(email: string, password: string): Promise<User> {
  if (!auth) throw new Error('Firebase Auth is not configured');
  const result = await signInWithEmailAndPassword(auth, email, password);
  return result.user;
}

/**
 * Create a new account with email and password.
 */
async function signUpWithEmail(email: string, password: string): Promise<User> {
  if (!auth) throw new Error('Firebase Auth is not configured');
  const result = await createUserWithEmailAndPassword(auth, email, password);
  return result.user;
}

/**
 * Sign in with Google OAuth popup.
 */
async function signInWithGoogle(): Promise<User> {
  if (!auth) throw new Error('Firebase Auth is not configured');
  const provider = new GoogleAuthProvider();
  const result = await signInWithPopup(auth, provider);
  return result.user;
}

/**
 * Sign out the current user.
 */
async function signOut(): Promise<void> {
  if (!auth) return;
  await firebaseSignOut(auth);
}

/**
 * Get the currently signed-in Firebase user (synchronous snapshot).
 */
function getCurrentUser(): User | null {
  if (!auth) return null;
  return auth.currentUser;
}

/**
 * Get a fresh ID token for the current user.
 */
async function getIdToken(forceRefresh = false): Promise<string | null> {
  if (!auth) return null;
  const user = auth.currentUser;
  if (!user) return null;
  return user.getIdToken(forceRefresh);
}

/**
 * Subscribe to auth state changes.
 * Returns an unsubscribe function.
 */
function onAuthStateChange(callback: (user: User | null) => void): Unsubscribe {
  if (!auth) {
    // Immediately fire with null and return a no-op unsubscribe
    callback(null);
    return () => {};
  }
  return onAuthStateChanged(auth, callback);
}

// =============================================================================
// Export
// =============================================================================

export const authService = {
  signInWithEmail,
  signUpWithEmail,
  signInWithGoogle,
  signOut,
  getCurrentUser,
  getIdToken,
  onAuthStateChange,
  isConfigured,
};
